// How a tool call that ended without a result reads in the chat, when that end
// was a decision rather than a failure.
//
// «Отклонить» on a dangerous-command prompt used to come back as «Ошибка:
// Терминал» over an English error string: the user's own decision, drawn as a
// tool failure — and read as a bug. The sidecar now records why the call ended
// (packages/opencode/src/session/processor.ts: `rejectedByUser`, `EndedBy`) in
// the part's `state.metadata`; the error text is left alone because it is what
// the MODEL reads. A part stored before that carries only the text and keeps
// its old rendering — nothing here guesses from the wording.
//
// Kept in its own module (like session-turn-interrupted.ts) so the decision is
// testable without importing a Solid component.

export type ToolOutcome =
  // The user turned THIS call down: its permission prompt, a dangerous-command
  // confirmation, or a question. `feedback` is what they rejected it with.
  | { kind: "rejected"; feedback?: string }
  // The turn was stopped while the call was open (Stop, or a cancel on its
  // behalf). Deliberately not "by you": a cancel is not always the user's click.
  | { kind: "stopped" }
  // The call was still open when the user's rejection of ANOTHER call in the
  // same step ended the turn.
  | { kind: "cut" }

// The tool whose calls are commands; every other tool's call is an action.
const COMMAND_TOOLS = new Set(["bash"])

export function toolOutcome(metadata: unknown): ToolOutcome | undefined {
  if (!metadata || typeof metadata !== "object") return undefined
  const meta = metadata as Record<string, unknown>
  if (meta.rejected === true) {
    const feedback = typeof meta.feedback === "string" && meta.feedback.trim() ? meta.feedback.trim() : undefined
    return feedback ? { kind: "rejected", feedback } : { kind: "rejected" }
  }
  if (meta.endedBy === "cancel") return { kind: "stopped" }
  if (meta.endedBy === "rejection") return { kind: "cut" }
  return undefined
}

/** The i18n keys (packages/ui/src/i18n) a tool card shows for an outcome. */
export function toolOutcomeText(outcome: ToolOutcome, tool: string) {
  switch (outcome.kind) {
    case "rejected":
      return {
        title: "ui.tool.outcome.rejected.title",
        body: COMMAND_TOOLS.has(tool) ? "ui.tool.outcome.rejected.command" : "ui.tool.outcome.rejected.action",
      }
    case "stopped":
      return { title: "ui.tool.outcome.stopped.title", body: "ui.tool.outcome.stopped.body" }
    case "cut":
      return { title: "ui.tool.outcome.stopped.title", body: "ui.tool.outcome.cut.body" }
  }
}
